import React, { createContext, useCallback, useContext, useMemo, useState, useEffect } from "react";
import CookieConsentBanner from "./CookieConsentBanner";
import CookieSettingsDialog from "./CookieSettingsDialog";
import { subscribe as subscribeConsent, readConsent } from "@/lib/consent";

/**
 * Consent Foundation — global provider.
 *
 * Purpose:
 *   * Mounts the first-visit banner and the granular settings dialog
 *     at the app root so they are reachable from every route without
 *     a page reload.
 *   * Exposes a small imperative API via context so any surface
 *     (footer button, in-page "manage cookies" link, etc.) can open
 *     the settings dialog without importing the dialog directly.
 *   * The consent DATA itself lives in the centralized `@/lib/consent`
 *     module, not here — this provider is a purely UI-level host.
 *
 * Language state (Slice B/C follow-up, 2026-02-08):
 *   The settings dialog is mounted ONCE at the app root and, before
 *   this change, always read its labels from the reactive persisted
 *   locale via `useTranslation()`. That let a stale persisted locale
 *   override an explicit `/de/` or `/en/` route, so `/de/cookies`
 *   could open an English modal (and vice versa).
 *
 *   Fix: at open time we resolve the language from the URL path
 *   (`/de/*` → `de`, `/en/*` → `en`, otherwise `null`) and pass it as
 *   an authoritative override to the dialog. Callers may also pass
 *   an explicit `openSettings("de" | "en")` — this wins over URL
 *   detection. When neither is available the dialog falls back to
 *   the persisted locale. This is a language-STATE fix, not a
 *   permanent hardcode: switching between `/de/*` and `/en/*` opens
 *   the dialog in the corresponding language without a hard refresh
 *   because we re-derive on every `openSettings()` call.
 */
const ConsentContext = createContext({
  openSettings: () => {},
  closeSettings: () => {},
});

export function useConsentUi() {
  return useContext(ConsentContext);
}

// Extract a route-forced language from `window.location.pathname`.
// Recognises the language-scoped legal / research-transparency routes.
// Returns `null` when the current URL doesn't force a language — in
// that case the dialog falls back to the persisted locale.
function resolveRouteLanguage() {
  if (typeof window === "undefined") return null;
  const p = window.location.pathname || "";
  if (p.startsWith("/de/") || p === "/de") return "de";
  if (p.startsWith("/en/") || p === "/en") return "en";
  return null;
}

export default function ConsentProvider({ children }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  // Language override passed to the dialog. `null` = fall back to the
  // persisted locale (canonical routes and unknown paths).
  const [dialogLanguage, setDialogLanguage] = useState(null);
  // Also expose a subscription-triggered re-render so any consumer
  // reading getCategory() through this provider stays fresh.
  const [, force] = useState(0);
  useEffect(() => {
    return subscribeConsent(() => force((n) => n + 1));
  }, []);

  const openSettings = useCallback((explicitLang) => {
    const lang =
      explicitLang === "en" || explicitLang === "de"
        ? explicitLang
        : resolveRouteLanguage();
    setDialogLanguage(lang);
    setDialogOpen(true);
  }, []);
  const closeSettings = useCallback(() => setDialogOpen(false), []);

  const value = useMemo(
    () => ({
      openSettings,
      closeSettings,
      // read-through helpers so consumers do not need to import the
      // low-level module for common queries.
      readConsent,
    }),
    [openSettings, closeSettings],
  );

  return (
    <ConsentContext.Provider value={value}>
      {children}
      <CookieConsentBanner onOpenSettings={openSettings} />
      <CookieSettingsDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        language={dialogLanguage}
      />
    </ConsentContext.Provider>
  );
}
